import React, { useEffect, useMemo, useRef, useState } from "react";
import { classNames } from "../../helpers/classNames";
import CompressImage from "../elements/CompressedImage";
import { toast } from "react-toastify";

const FileUploadSingle = ({
  required,
  value,
  onRemove = () => {},
  onChange = () => {},
  labelClasses,
  label,
  fileType,
  name,
  errorType,
  errorText,
  size = "full",
  placeholder = "Choose File",
  containerClasses,
  accept,
}) => {
  const inputRef = useRef(null);
  const [image, setImage] = useState(null);

  const handleImageChange = (event) => {
    const acceptList = accept?.split(",").map((elm) => elm.trim()); // Normalize input
    const file = event.target.files[0];

    if (!file) return;

    if (acceptList === undefined || !acceptList) {
      setImage(file);
      onChange(file);
      event.target.value = "";
      return;
    }

    const fileExt = file.name.split(".").pop().toLowerCase(); // Extract file extension
    const fileType = file.type; // Get MIME type

    const isValid = acceptList?.some((type) => {
      if (type.startsWith(".")) {
        return fileExt === type.replace(".", "").toLowerCase(); // Match extension
      }
      return fileExt === type || fileType === type; // Match MIME type
    });

    if (!isValid) {
      toast.info(`Upload only ${acceptList?.join(", ")}`);
      event.target.value = "";
      return;
    }

    setImage(file);
    onChange(file);
    event.target.value = "";
  };

  const handleRemoveImage = (e) => {
    setImage(null);
    onChange("");
    onRemove();
  };

  return (
    <div className="relative w-full">
      {label && (
        <div
          className={classNames(
            "text-xs font-medium text-shades-07 mb-1 font-Poppins",
            labelClasses
          )}
        >
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-1 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}
      <div
        className={classNames(
          size === "full"
            ? "cursor-pointer flex flex-col items-center justify-center overflow-hidden bg-chestnut-rose/5 border-2 border-chestnut-rose border-dashed rounded-lg h-40"
            : "cursor-pointer relative w-full h-10 flex items-center focus:ring-0 transition-all duration-200 gap-2 overflow-hidden p-1 rounded-md bg-white border border-shades-05/50 ",
          containerClasses
        )}
      >
        {image || value ? (
          <>
            <div
              className={classNames(
                size === "full"
                  ? "relative h-full w-full"
                  : "h-full min-w-10 w-10 rounded shadow border border-slate-200 overflow-hidden"
              )}
            >
              <CompressImage
                src={
                  (image || value) instanceof File
                    ? URL.createObjectURL(image || value)
                    : image || value
                }
                quality={0.4}
                alt="Preview"
                className={classNames(
                  "w-full h-full object-cover object-center"
                )}
              />
              {size === "full" && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute hover:scale-[1.2] transition-all duration-200 top-1/2 left-1/2 bottom-1/2 right-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-red-500 text-white rounded-full px-2 py-1 text-xs"
                >
                  <i className="fa-fw fa-regular fa-trash-can"></i>
                </button>
              )}
            </div>
            {size === "small" && (
              <div className="text-xs text-secondayColor truncate w-[calc(100%-90px)]">
                {(image || value) instanceof File
                  ? (image || value)?.name
                  : name
                  ? name
                  : image || value}
              </div>
            )}
            {size === "small" && (
              <button
                type="button"
                onClick={handleRemoveImage}
                className="text-red-500 ml-auto w-10"
              >
                <i className="fa-fw fa-regular fa-trash-can"></i>
              </button>
            )}
          </>
        ) : (
          <label
            className={classNames(
              size === "full"
                ? "relative cursor-pointer w-full h-full flex flex-col items-center justify-center"
                : ""
            )}
          >
            {size === "full" && (
              <>
                <img
                  src={require("../../assets/images/imageUpload.png")}
                  alt="icon"
                  className={classNames(
                    size === "full" ? "w-10 h-10 opacity-80" : ""
                  )}
                />
                <div className="text-sm text-shades-07 mt-2">
                  <span className="text-chestnut-rose font-semibold underline">
                    Click to upload
                  </span>{" "}
                  or drag and drop
                </div>
              </>
            )}
            {size === "small" && (
              <span className="text-sm font-Poppins text-shades-07 pl-2">
                {placeholder}
              </span>
            )}
            <input
              type="file"
              ref={inputRef}
              className={classNames(
                "opacity-0 absolute h-full w-[200%] top-0 right-0 cursor-pointer z-10"
              )}
              accept={accept}
              onChange={handleImageChange}
            />
          </label>
        )}
      </div>
      {errorType && errorType === "danger" && (
        <div className="text-xs font-medium text-red-600">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "warning" && (
        <div className="text-xs font-medium text-amber-500">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "success" && (
        <div className="text-xs font-medium text-green-600">
          <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "info" && (
        <div className="text-xs font-medium text-sky-600">
          <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
          {errorText}
        </div>
      )}
    </div>
  );
};

export default FileUploadSingle;
